import type { CorSelo } from '../../componentes/base';

export type TipoRecurso = 'profissional' | 'sala' | 'equipamento';
export type CorRecurso = 'acento' | 'azul' | 'ciano' | 'roxo' | 'laranja' | 'verde' | 'neutro';
export type EstadoAgendamento = 'marcado' | 'confirmado' | 'realizado' | 'faltou' | 'cancelado';
export type Frequencia = 'diaria' | 'semanal' | 'quinzenal' | 'mensal';

export interface IntervaloHorario {
  de: string;
  ate: string;
}
/** Chaves "0" (domingo) a "6" (sábado). */
export type HorarioTrabalho = Record<string, IntervaloHorario[]>;

export interface Recurso {
  id: string;
  tipo: TipoRecurso;
  nome: string;
  usuario_id: string | null;
  usuario_nome: string | null;
  cor: CorRecurso;
  /** Ficha 2026-11-03: cor da agenda principal da pessoa no Google (#rrggbb); vale sobre `cor`. */
  cor_google: string | null;
  horario_trabalho: HorarioTrabalho;
  ativo: boolean;
  created_at: string;
}

/** Fase 2 (W26): agenda da conta Google conectada em que ela pode gravar. */
export interface CalendarioGoogle {
  id: string;
  nome: string;
  principal: boolean;
  cor: string | null;
  acesso: 'owner' | 'writer';
}

/** Fase 2 (W26): conector conectado e a agenda escolhida de cada recurso (recurso_id -> calendarId). */
export interface EstadoGoogle {
  conectado: boolean;
  mapa: Record<string, string>;
  /** Lista 30/10 B (item 34): recursos ligados ao Google Agenda pessoal (Minha conta); a escolha do mapa só vale se desconectar. */
  pessoais?: string[];
}

/** Lista 30/10 B (item 34): Minha conta > Perfil > Google Agenda. */
export interface MinhaAgendaGoogle {
  disponivel: boolean;
  estado: 'conectado' | 'desconectado';
  motivo: 'pessoa' | 'revogado' | null;
  conta: string | null;
  conectado_em: string | null;
  recursos: { id: string; nome: string }[];
  tem_profissional: boolean;
  sincronizado_em: string | null;
  recuo_ate: string | null;
  /** Ficha 2026-11-03: conectada sem o escopo que lê a cor da agenda; a tela avisa para reconectar. */
  falta_cor: boolean;
}

export interface MeuAgendamento {
  id: string;
  inicio: string;
  fim: string;
  estado: EstadoAgendamento;
  assunto: string | null;
  servico_nome: string | null;
  parceiro_id: string | null;
  parceiro_nome: string | null;
  recurso_nome: string | null;
  dia_inteiro?: boolean;
  no_google: boolean;
}

/** Fase 2 (W18, K2): serviço agendável do catálogo único (Cadastros > Serviços), pela rota `/agenda/servicos`. */
export interface Servico {
  id: string;
  nome: string;
  codigo: string | null;
  descricao: string | null;
  duracao_min: number;
  preco_centavos: number;
  moeda: string;
  recurso_ids: string[];
  ativo: boolean;
  agendavel: boolean;
  created_at: string;
}

export interface Agendamento {
  id: string;
  recurso_id: string;
  /** Nulos quando o recurso ou o serviço ficou numa empresa que a pessoa não vê (review r17 robustez, M3): `nomeCadastro`. */
  recurso_nome: string | null;
  recurso_cor: CorRecurso | null;
  recurso_cor_google: string | null;
  /** Nulo na reunião livre (03/11, sem serviço nem cliente): `ehReuniaoLivre`. `servico_nome` nulo com id é cadastro de outra empresa. */
  servico_id: string | null;
  servico_nome: string | null;
  duracao_min: number;
  preco_centavos: number;
  /** Nulo na reunião livre sem vínculo; aí não há cliente para mostrar (não é "Cadastro de outra empresa"). */
  parceiro_id: string | null;
  /** Nulo quando o cliente é de uma empresa que a pessoa não vê (W20): a tela usa `nomeParceiro`. */
  parceiro_nome: string | null;
  parceiro_telefone: string | null;
  oportunidade_id: string | null;
  inicio: string;
  fim: string;
  estado: EstadoAgendamento;
  observacoes: string | null;
  serie_id: string | null;
  recorrencia: Frequencia | null;
  recorrencia_ate: string | null;
  google_event_id: string | null;
  lembretes_enviados: number[];
  /** Campos personalizados (catálogo `agenda.agendamento`). */
  extras: Record<string, unknown>;
  /** Lote 19 (12a): marcado sobre horário já ocupado por outro agendamento. */
  encaixe: boolean;
  /** Lote 19 (12b). */
  local: string | null;
  link_reuniao: string | null;
  /** Lote 19 (12e): nenhuma, google_meet (o link volta da sincronia), sala_propria ou link. */
  reuniao_tipo: TipoReuniao;
  convidados: Convidado[];
  created_at: string;
  /** CRM no novo desenho (lote 1): assunto da reunião, o usuário do profissional (responsável) e quem agendou. */
  assunto?: string | null;
  responsavel_usuario_id?: string | null;
  criado_por?: string | null;
  /** Reunião livre (03/11): dia inteiro (do início do primeiro dia ao início do dia seguinte ao último, no fuso do workspace). */
  dia_inteiro?: boolean;
  /** Reunião livre (03/11): o convite saiu pelo Google da conta pessoal; sem ele, é o e-mail do sistema. */
  convite_google?: boolean;
}

/** Reunião livre (03/11): agendamento sem serviço. Quem não vê a empresa do serviço recebe só o nome nulo; o id continua. */
export const ehReuniaoLivre = (a: Pick<Agendamento, 'servico_id'>) => a.servico_id === null;

export type TipoReuniao = 'nenhuma' | 'google_meet' | 'sala_propria' | 'link';

/** Lote 19 (12b): pessoa da equipe (membro do workspace) ou contato de fora. Só na reunião livre com `convite_google` vai ao Google. */
export type Convidado = { tipo: 'interno'; usuario_id: string; nome: string } | { tipo: 'externo'; email: string; nome: string | null } | { tipo: 'contato'; parceiro_id: string; nome: string; email: string | null };

export interface Bloqueio {
  id: string;
  recurso_id: string;
  recurso_nome: string;
  recurso_cor: CorRecurso;
  recurso_cor_google: string | null;
  inicio: string;
  fim: string;
  motivo: string | null;
  origem: 'manual' | 'google';
  created_at: string;
  /** Ficha 2026-11-03: evento do Google na agenda de quem está olhando; só ela abre e edita. */
  meu_google?: boolean;
  /** O nome do evento, que a API só devolve à dona. */
  titulo_google?: string | null;
}

/** Ficha 2026-11-03: `GET /agenda/bloqueios/:id/evento-google`. */
export interface EventoGoogleDados {
  assunto: string;
  observacoes: string;
  descricao_cortada: boolean;
  descricao_formatada: boolean;
  outro_corte: boolean;
  local: string;
  inicio: string;
  fim: string;
  dia_inteiro: boolean;
  convidados: Convidado[];
  reuniao_tipo: 'google_meet' | 'nenhuma';
  link_reuniao: string | null;
  repetido: boolean;
  versao_google: string;
  so_leitura: 'convidado' | 'tipo_especial' | 'convidados_demais' | 'conferencia_outra' | 'antigo' | null;
}

export interface Slot {
  recurso_id: string;
  recurso_nome: string;
  inicio: string;
  fim: string;
}

export interface Disponibilidade {
  fuso: string;
  duracao_min: number;
  slots: Slot[];
}

export const COR_ESTADO: Record<EstadoAgendamento, CorSelo> = { marcado: 'azul', confirmado: 'verde', realizado: 'ciano', faltou: 'laranja', cancelado: 'neutro' };
export const CORES_RECURSO: CorRecurso[] = ['acento', 'azul', 'ciano', 'roxo', 'laranja', 'verde', 'neutro'];
export const TIPOS_RECURSO: TipoRecurso[] = ['profissional', 'sala', 'equipamento'];
export const ESTADOS_ABERTOS: EstadoAgendamento[] = ['marcado', 'confirmado'];

/**
 * Ajustes do CRM 27/10 (C16, decisão 9): ações de "Mais opções" pelo estado. Realizada e não ocorrida só depois do início
 * (antes, a opção some); "Voltar para agendada" na realizada e na não ocorrida; cancelar só a aberta. Função pura, testada.
 */
export function acoesDaReuniao(estado: EstadoAgendamento, inicio: string, agora = Date.now()) {
  const comecou = Date.parse(inicio) <= agora;
  const aberta = estado === 'marcado' || estado === 'confirmado';
  return {
    realizar: comecou && (aberta || estado === 'faltou'),
    faltou: comecou && (aberta || estado === 'realizado'),
    reabrir: estado === 'realizado' || estado === 'faltou',
    cancelar: aberta,
  };
}
